// Yelp Fusion API Service
// Documentation: https://docs.developer.yelp.com/docs/places-intro

import { logger } from './logger';

class YelpAPI {
  constructor() {
    // Use Vercel serverless functions (same domain as frontend)
    // In development, can fall back to Express server if needed
    this.backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    this.useBackendProxy = true; // Always use backend proxy for production readiness
  }

  getHeaders() {
    // For backend proxy, we don't need to send the API key
    // The backend server handles authentication securely
    return {
      'Content-Type': 'application/json'
    };
  }

  async searchBusinesses(params = {}) {
    const {
      term = '',
      location = '',
      latitude = null,
      longitude = null,
      radius = 5000,
      categories = 'restaurants',
      limit = 20,
      sort_by = 'best_match',
      price = '',
      open_now = false
    } = params;

    logger.debug(`🔍 Yelp API search - term: "${term}", location: "${location}"`);

    try {
      // Build query parameters for Yelp API
      const queryParams = new URLSearchParams();

      if (term) queryParams.append('term', term);
      if (location) queryParams.append('location', location);
      if (latitude && longitude) {
        queryParams.append('latitude', latitude);
        queryParams.append('longitude', longitude);
      }
      if (radius) queryParams.append('radius', Math.min(radius, 40000)); // Max 40km
      if (categories) queryParams.append('categories', categories);
      if (limit) queryParams.append('limit', Math.min(limit, 50)); // Max 50
      if (sort_by) queryParams.append('sort_by', sort_by);
      if (price) queryParams.append('price', price);
      if (open_now) queryParams.append('open_now', open_now);

      // Use backend proxy server to avoid CORS issues
      const apiUrl = `${this.backendUrl}/api/yelp/search?${queryParams}`;

      logger.debug(`Making Yelp API request via backend proxy: ${apiUrl}`);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`Yelp API error: ${response.status} ${response.statusText} - ${errorText}`);
        throw new Error(`Yelp API error: ${response.status} ${response.statusText}`);
      }

      const yelpData = await response.json();
      logger.debug(`✅ Yelp API returned ${yelpData.businesses?.length || 0} real businesses`);

      if (!yelpData.businesses || yelpData.businesses.length === 0) {
        logger.info('No businesses found from Yelp API');
        return [];
      }

      return this.formatBusinessResults(yelpData.businesses);

    } catch (error) {
      logger.error(`Yelp API call failed: ${error.message}`);

      // For production, we should not show fake data
      // Instead, return empty array and let the UI show an appropriate message
      return [];
    }
  }

  async getBusinessDetails(businessId) {
    try {
      const url = `${this.backendUrl}/api/yelp/business/${businessId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        throw new Error(`Yelp API error: ${response.status} ${response.statusText}`);
      }

      const business = await response.json();
      return this.formatBusinessDetails(business);

    } catch (error) {
      logger.error(`Failed to get business details: ${error.message}`);
      return null;
    }
  }

  async autocomplete(text, latitude = null, longitude = null) {
    try {
      // Yelp autocomplete has strict validation - skip if text is too short or invalid
      if (!text || text.length < 3) {
        return [];
      }

      const queryParams = new URLSearchParams({
        text: text
      });

      if (latitude && longitude) {
        queryParams.append('latitude', latitude);
        queryParams.append('longitude', longitude);
      }

      const url = `${this.backendUrl}/api/yelp/autocomplete?${queryParams}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (!response.ok) {
        // Silently fail - autocomplete is optional
        return [];
      }

      const data = await response.json();
      return data.terms || [];

    } catch (error) {
      // Silently fail - autocomplete is optional, main search still works
      return [];
    }
  }

  formatBusinessResults(businesses) {
    return businesses.map(business => ({
      id: business.id,
      name: business.name,
      address: this.formatAddress(business.location),
      phone: business.phone || business.display_phone || 'Contact for details',
      cuisineType: this.extractCuisineType(business.categories),
      priceRange: business.price || '$$',
      rating: business.rating || 4.0,
      reviewCount: business.review_count || 0,
      imageUrl: business.image_url,
      url: business.url,
      deliveryAvailable: this.checkDeliveryAvailable(business.transactions),
      deliveryFee: this.estimateDeliveryFee(business.price),
      estimatedDeliveryTime: '25-40 minutes',
      isYelpBusiness: true,
      coordinates: business.coordinates,
      categories: business.categories,
      photos: business.photos || [],
      distance: business.distance ? Math.round(business.distance) : null,
      menu: this.generateBasicMenu(business.categories)
    }));
  }

  formatBusinessDetails(business) {
    return {
      ...this.formatBusinessResults([business])[0],
      hours: business.hours,
      special_hours: business.special_hours,
      photos: business.photos || [],
      attributes: business.attributes,
      messaging: business.messaging
    };
  }

  formatAddress(location) {
    if (!location) return 'Address not available';

    const parts = [
      location.address1,
      location.city,
      location.state,
      location.zip_code
    ].filter(Boolean);

    return parts.join(', ');
  }

  extractCuisineType(categories) {
    if (!categories || !categories.length) return 'Restaurant';
    return categories[0].title || 'Restaurant';
  }

  checkDeliveryAvailable(transactions) {
    return transactions && transactions.includes('delivery');
  }

  estimateDeliveryFee(priceRange) {
    const fees = {
      '$': 1.99,
      '$$': 2.99,
      '$$$': 3.99,
      '$$$$': 4.99
    };
    return fees[priceRange] || 2.99;
  }

  generateBasicMenu(categories) {
    const cuisineType = this.extractCuisineType(categories);

    const menuTemplates = {
      'Italian': [
        { name: 'Margherita Pizza', price: 14.99 },
        { name: 'Chicken Parmigiana', price: 18.99 },
        { name: 'Pasta Carbonara', price: 16.99 }
      ],
      'Mexican': [
        { name: 'Chicken Tacos', price: 12.99 },
        { name: 'Burrito Bowl', price: 13.99 },
        { name: 'Quesadilla', price: 11.99 }
      ],
      'Asian': [
        { name: 'Pad Thai', price: 13.99 },
        { name: 'Fried Rice', price: 11.99 },
        { name: 'General Tso\'s Chicken', price: 15.99 }
      ],
      'American': [
        { name: 'Classic Burger', price: 12.99 },
        { name: 'Grilled Chicken', price: 16.99 },
        { name: 'Caesar Salad', price: 10.99 }
      ]
    };

    const items = menuTemplates[cuisineType] || menuTemplates['American'];

    return [{
      category: 'Popular Items',
      items: items
    }];
  }



  isConfigured() {
    // Always return true when using backend proxy
    // The backend will handle API key validation
    return true;
  }

  getBackendUrl() {
    return this.backendUrl;
  }
}

export const yelpAPI = new YelpAPI();
export default yelpAPI;